import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ThemeProvider,
    Card,
    CardContent,
    useMediaQuery,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import NavBar from '../../layout/NavBar';
import NavBarBot from '../../layout/NavBarBot';
import BackButton from '../../util/buttons/BackButton';
import HeaderCard from '../../layout/HeaderCard';
import LoadingPage from '../../layout/LoadingPage';
import Notification from '../../util/notifications/Notification';
import { darkTheme } from '../../../theme/darkTheme';
import { CardioApi } from '../../../services/api';
import { TYPEN_MIT_DISTANZ, leereForm, btnSx } from './components/cardioConstants';
import SessionKarte from './components/SessionKarte';
import CardioFormDialog from './components/CardioFormDialog';

// ─── Haupt-Komponente ─────────────────────────────────────────────────────────
function Cardio() {
    const isMobile = useMediaQuery(darkTheme.breakpoints.down('md'));

    const [sessions, setSessions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const [dialogOpen, setDialogOpen] = useState(false);
    const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
    const [editSession, setEditSession] = useState(null); // null = neue Session
    const [form, setForm] = useState(leereForm);

    // ── Daten laden ──
    const laden = useCallback(async () => {
        setLoading(true);
        try {
            const [sessionsRes, statsRes] = await Promise.all([
                CardioApi.getSessions(),
                CardioApi.getStats(),
            ]);
            setSessions(Array.isArray(sessionsRes.data) ? sessionsRes.data : []);
            setStats(statsRes.data);
        } catch {
            setMessage({ type: 'error', text: 'Daten konnten nicht geladen werden.' });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { laden(); }, [laden]);

    // ── Dialog öffnen ──
    const handleNeu = () => {
        setForm(leereForm);
        setEditSession(null);
        setDialogOpen(true);
    };

    const handleBearbeiten = (session) => {
        setForm({
            datum: session.datum?.split('T')[0] || session.datum,
            cardio_typ: session.cardio_typ || '',
            dauer_minuten: session.dauer_minuten ?? '',
            distanz_km: session.distanz_km ?? '',
            durchschnitts_bpm: session.durchschnitts_bpm ?? '',
            max_bpm: session.max_bpm ?? '',
            kalorien: session.kalorien ?? '',
            intensitaet: session.intensitaet ?? '',
            notizen: session.notizen ?? '',
        });
        setEditSession(session);
        setDialogOpen(true);
    };

    // ── Speichern ──
    const handleSaveConfirm = () => setSaveConfirmOpen(true);

    const handleSave = async () => {
        setSaveConfirmOpen(false);
        if (!form.cardio_typ || !form.dauer_minuten || !form.datum) {
            setMessage({ type: 'error', text: 'Datum, Typ und Dauer sind Pflichtfelder.' });
            return;
        }
        setLoading(true);
        try {
            if (editSession) {
                await CardioApi.updateSession(editSession.id, form);
                setMessage({ type: 'success', text: 'Session aktualisiert.' });
            } else {
                await CardioApi.createSession(form);
                setMessage({ type: 'success', text: 'Cardio-Session gespeichert!' });
            }
            setDialogOpen(false);
            await laden();
        } catch (e) {
            setMessage({ type: 'error', text: e?.response?.data?.error || 'Fehler beim Speichern.' });
        } finally {
            setLoading(false);
        }
    };

    // ── Löschen ──
    const handleLoeschen = async (id) => {
        try {
            await CardioApi.deleteSession(id);
            setMessage({ type: 'success', text: 'Session gelöscht.' });
            await laden();
        } catch {
            setMessage({ type: 'error', text: 'Löschen fehlgeschlagen.' });
        }
    };

    const hatDistanz = TYPEN_MIT_DISTANZ.includes(form.cardio_typ);
    const formValid = !!form.cardio_typ && !!form.dauer_minuten && !!form.datum;

    if (loading && sessions.length === 0) return <LoadingPage />;

    return (
        <>
            <ThemeProvider theme={darkTheme}>
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 4 }}>
                    <NavBar />
                    <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 }, pb: '44px' }}>
                        {!isMobile && <BackButton />}
                        <HeaderCard title="Cardio erfassen" />

                        {/* ── Notification ── */}
                        {(message.type === 'error' || message.type === 'success') && (
                            <Notification
                                type={message.type}
                                message={message.text}
                                onClose={() => setMessage({ type: '', text: '' })}
                            />
                        )}

                        {/* ── Stats-Karten ── */}
                        {stats?.gesamt && (
                            <Card sx={{
                                mb: 2,
                                borderRadius: '16px',
                                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                                border: '1px solid rgba(59, 130, 246, 0.2)',
                            }}>
                                <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                                    <Typography variant="subtitle2" color="text.secondary" mb={2} sx={{ textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.75rem' }}>
                                        Gesamt
                                    </Typography>
                                    <Grid container spacing={2}>
                                        {[
                                            { label: 'Sessions', wert: stats.gesamt.anzahl_sessions ?? '—', einheit: '' },
                                            { label: 'Minuten', wert: stats.gesamt.gesamt_minuten ?? '—', einheit: '' },
                                            { label: 'Kilometer', wert: stats.gesamt.gesamt_km ?? '—', einheit: ' km' },
                                            { label: 'Kalorien', wert: stats.gesamt.gesamt_kalorien ?? '—', einheit: ' kcal' },
                                        ].map(({ label, wert, einheit }) => (
                                            <Grid size={{ xs: 6, sm: 3 }} key={label}>
                                                <Box textAlign="center">
                                                    <Typography sx={{ fontSize: { xs: '1.4rem', sm: '1.8rem' }, fontWeight: 700, lineHeight: 1, color: '#3b82f6' }}>
                                                        {wert}{einheit}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                                                        {label}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        )}

                        {/* ── Aktions-Button (Desktop) ── */}
                        {!isMobile && (
                            <Grid container sx={{ pb: '15px' }}>
                                <Grid size={{ xs: 12 }}>
                                    <Button
                                        onClick={handleNeu}
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        startIcon={<AddIcon />}
                                        sx={btnSx}
                                    >
                                        Neue Cardio-Session
                                    </Button>
                                </Grid>
                            </Grid>
                        )}

                        {/* ── Session-Liste ── */}
                        {sessions.length === 0 && !loading ? (
                            <Card sx={{ borderRadius: '16px', bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
                                <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                    <Typography sx={{ fontSize: '2.5rem', mb: 1 }}>🏃</Typography>
                                    <Typography variant="h6" color="text.secondary" mb={2}>
                                        Noch keine Cardio-Sessions
                                    </Typography>
                                    <Button onClick={handleNeu} variant="contained" sx={btnSx} startIcon={<AddIcon />}>
                                        Erste Session eintragen
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            sessions.map(session => (
                                <SessionKarte
                                    key={session.id}
                                    session={session}
                                    onEdit={() => handleBearbeiten(session)}
                                    onDelete={() => handleLoeschen(session.id)}
                                />
                            ))
                        )}
                    </Container>

                    {/* ── Formular-Dialog ── */}
                    <CardioFormDialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        editSession={editSession}
                        form={form}
                        onForm={setForm}
                        onSaveConfirm={handleSaveConfirm}
                        formValid={formValid}
                        loading={loading}
                        hatDistanz={hatDistanz}
                        isMobile={isMobile}
                    />

                    {/* ── Bestätigungs-Dialog ── */}
                    <Dialog open={saveConfirmOpen} onClose={() => setSaveConfirmOpen(false)}>
                        <DialogTitle>Session speichern?</DialogTitle>
                        <DialogContent>
                            <Typography>
                                Möchtest du die Cardio-Session wirklich speichern?
                            </Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setSaveConfirmOpen(false)}>Abbrechen</Button>
                            <Button
                                onClick={handleSave}
                                variant="contained"
                                sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)', borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}
                            >
                                Speichern
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
            </ThemeProvider>

            {/* ── Mobile Bottom Bar ── */}
            <NavBarBot
                mainBtnF={handleNeu}
                mainBtnTxt={<AddIcon />}
                mainBtnDisabled={false}
            />
        </>
    );
}

export default Cardio;
